import React from 'react';
import { createPortal } from 'react-dom';
import { Trash2, Download, X, CheckCheck, Keyboard } from 'lucide-react';

/**
 * Floating action bar shown when one or more rows are selected.
 *
 * Props:
 *  - count: number of selected items
 *  - onClear(): unselect all
 *  - onDelete(): bulk delete
 *  - onExport(): bulk export CSV
 *  - statusOptions?: string[] allowed statuses for bulk status change
 *  - onChangeStatus?(status): change status of selected items
 *  - showShortcutsHint?: boolean — show ⌘A/Suppr/Échap helper (default true)
 */
export default function BulkActionsBar({ count, onClear, onDelete, onExport, statusOptions, onChangeStatus, extraActions = [], showShortcutsHint = true }) {
  if (count === 0) return null;
  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform || '');
  const modKey = isMac ? '⌘' : 'Ctrl';
  const content = (
    <div
      data-testid="bulk-actions-bar"
      className="fixed left-1/2 -translate-x-1/2 bottom-6 z-[90] bg-[#1A1B2E] text-white rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3"
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      <button onClick={onClear} className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center" title="Effacer la sélection (Échap)">
        <X className="w-4 h-4" />
      </button>
      <div className="text-[13px] font-semibold">{count} sélectionné{count > 1 ? 's' : ''}</div>
      <div className="h-5 w-px bg-white/15 mx-1" />
      {extraActions.map((a) => (
        <button key={a.key} data-testid={a.testId} onClick={a.onClick} className="h-9 px-3 rounded-lg bg-white/10 hover:bg-white/20 text-[13px] font-semibold flex items-center gap-1.5 transition-colors">
          {a.icon && <a.icon className="w-4 h-4" />} {a.label}
        </button>
      ))}
      {onExport && (
        <button data-testid="bulk-export-btn" onClick={onExport} className="h-9 px-3 rounded-lg bg-white/10 hover:bg-white/20 text-[13px] font-semibold flex items-center gap-1.5 transition-colors">
          <Download className="w-4 h-4" /> Exporter
        </button>
      )}
      {statusOptions?.length > 0 && onChangeStatus && (
        <div className="relative group">
          <button className="h-9 px-3 rounded-lg bg-white/10 hover:bg-white/20 text-[13px] font-semibold flex items-center gap-1.5 transition-colors">
            <CheckCheck className="w-4 h-4" /> Changer statut
          </button>
          <div className="absolute bottom-full mb-2 right-0 bg-white text-[#1A1B2E] rounded-xl shadow-lg py-1 min-w-[160px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
            {statusOptions.map((s) => (
              <button key={s} onClick={() => onChangeStatus(s)} className="w-full text-left px-3 py-2 text-[12.5px] hover:bg-[#EEF2FF] hover:text-[#4F46E5]">
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
      {onDelete && (
        <button data-testid="bulk-delete-btn" onClick={onDelete} className="h-9 px-3 rounded-lg bg-[#EF4444] hover:bg-[#DC2626] text-[13px] font-semibold flex items-center gap-1.5 transition-colors" title="Supprimer la sélection (Suppr)">
          <Trash2 className="w-4 h-4" /> Supprimer
        </button>
      )}
      {showShortcutsHint && (
        <div className="relative group ml-1">
          <button
            type="button"
            aria-label="Raccourcis clavier"
            data-testid="bulk-shortcuts-hint"
            className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/70 hover:text-white"
          >
            <Keyboard className="w-4 h-4" />
          </button>
          <div className="absolute bottom-full mb-2 right-0 bg-white text-[#1A1B2E] rounded-xl shadow-lg p-3 w-[260px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8FA5] mb-2">Raccourcis clavier</div>
            <div className="space-y-1.5 text-[12.5px]">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[#4A4B63]">Tout sélectionner</span>
                <kbd className="px-1.5 py-0.5 rounded bg-[#F6F7FB] border border-[#EFF0F6] text-[11px] font-mono font-semibold text-[#1A1B2E]">{modKey}+A</kbd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[#4A4B63]">Supprimer la sélection</span>
                <kbd className="px-1.5 py-0.5 rounded bg-[#F6F7FB] border border-[#EFF0F6] text-[11px] font-mono font-semibold text-[#1A1B2E]">Suppr</kbd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[#4A4B63]">Annuler la sélection</span>
                <kbd className="px-1.5 py-0.5 rounded bg-[#F6F7FB] border border-[#EFF0F6] text-[11px] font-mono font-semibold text-[#1A1B2E]">Échap</kbd>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  // Portal to body so the fixed positioning attaches to the viewport regardless of
  // any transformed ancestor (e.g., .slide-up on the main container creates a
  // CSS containing block that would otherwise trap position:fixed).
  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}
